import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Calendar, Filter } from "lucide-react";

const PERIOD_OPTIONS = [
  { id: "all", label: "All Time" },
  { id: "fy", label: "FY" },
  { id: "monthly", label: "Monthly" },
  { id: "yearly", label: "Yearly" },
  { id: "custom", label: "Custom" },
];

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Per-tab sessionStorage cache so the picker survives the parent's
// loading→data unmount cycle (each report re-renders its DateFilter from
// scratch on every fetch, blowing away local component state otherwise).
const STORAGE_PREFIX = "mis_date_filter:";
// Module-scoped set: ensures auto-restore fires AT MOST ONCE per page path
// per page-load — otherwise every loading→spinner remount would re-emit the
// last filter and trigger an infinite fetch loop (MIS Report bug).
const _emittedPaths = new Set();
function _loadCache() {
  try {
    const path = (typeof window !== "undefined" && window.location && window.location.pathname) || "default";
    const raw = sessionStorage.getItem(STORAGE_PREFIX + path);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}
function _saveCache(state) {
  try {
    const path = (typeof window !== "undefined" && window.location && window.location.pathname) || "default";
    sessionStorage.setItem(STORAGE_PREFIX + path, JSON.stringify(state));
  } catch (_) { /* ignore */ }
}

function getFYOptions() {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear; y >= currentYear - 10; y--) {
    options.push({
      label: `FY ${y}-${(y + 1).toString().slice(-2)}`,
      from_date: `${y}-04-01`,
      to_date: `${y + 1}-03-31`,
    });
  }
  return options;
}

function getYearOptions() {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear; y >= currentYear - 10; y--) {
    options.push({
      label: `${y}`,
      from_date: `${y}-01-01`,
      to_date: `${y}-12-31`,
    });
  }
  return options;
}

function getMonthOptions() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const options = [];
  for (let offset = 0; offset < 24; offset++) {
    let m = currentMonth - offset;
    let y = currentYear;
    while (m < 0) { m += 12; y -= 1; }
    const monthStr = String(m + 1).padStart(2, "0");
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    options.push({
      label: `${MONTHS[m]} ${y}`,
      from_date: `${y}-${monthStr}-01`,
      to_date: `${y}-${monthStr}-${String(daysInMonth).padStart(2, "0")}`,
    });
  }
  return options;
}

export default function DateFilter({ onFilterChange }) {
  const cached = _loadCache() || {};
  const [activePeriod, setActivePeriod] = useState(cached.activePeriod || "all");
  const [selectedOption, setSelectedOption] = useState(cached.selectedOption || "");
  const [customFrom, setCustomFrom] = useState(cached.customFrom || "");
  const [customTo, setCustomTo] = useState(cached.customTo || "");
  const initRef = useRef(null);

  const applyFilter = useCallback(
    (params) => {
      onFilterChange(params);
    },
    [onFilterChange]
  );

  const persist = (next) => {
    _saveCache({
      activePeriod, selectedOption, customFrom, customTo, ...next,
    });
  };

  const handlePeriodChange = (periodId) => {
    setActivePeriod(periodId);
    setSelectedOption("");
    if (periodId === "all") {
      persist({ activePeriod: periodId, selectedOption: "", lastApplied: {} });
      applyFilter({});
    } else {
      persist({ activePeriod: periodId, selectedOption: "" });
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option.label);
    persist({
      selectedOption: option.label,
      lastApplied: { from_date: option.from_date, to_date: option.to_date },
    });
    applyFilter({ from_date: option.from_date, to_date: option.to_date });
  };

  const handleCustomApply = () => {
    if (customFrom && customTo) {
      persist({
        customFrom, customTo,
        lastApplied: { from_date: customFrom, to_date: customTo },
      });
      applyFilter({ from_date: customFrom, to_date: customTo });
    }
  };

  // Re-emit cached filter once on mount so the parent's initial empty
  // `filters={}` state is replaced with what the user had picked previously
  // (e.g. after a hot-reload or parent re-mount).
  if (initRef.current == null) {
    initRef.current = true;
    const pageKey = (typeof window !== "undefined" && window.location && window.location.pathname) || "default";
    // Auto re-emit the last applied filter only ONCE per page-load, so
    // the parent's loading→remount loop doesn't keep re-firing it.
    if (!_emittedPaths.has(pageKey)) {
      _emittedPaths.add(pageKey);
      if (activePeriod === "custom" && customFrom && customTo) {
        Promise.resolve().then(() => applyFilter({ from_date: customFrom, to_date: customTo }));
      } else if (selectedOption && cached.lastApplied) {
        Promise.resolve().then(() => applyFilter(cached.lastApplied));
      }
    }
  }

  const getOptions = () => {
    switch (activePeriod) {
      case "fy": return getFYOptions();
      case "monthly": return getMonthOptions();
      case "yearly": return getYearOptions();
      default: return [];
    }
  };

  return (
    <div className="mb-6 space-y-3" data-testid="date-filter">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-500" />
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">Period:</span>
        {PERIOD_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            variant={activePeriod === opt.id ? "default" : "outline"}
            size="sm"
            onClick={() => handlePeriodChange(opt.id)}
            className="text-xs h-7 px-3"
            data-testid={`filter-${opt.id}`}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {activePeriod !== "all" && activePeriod !== "custom" && (
        <div className="flex items-center gap-2 flex-wrap">
          {getOptions().map((opt) => (
            <Button
              key={opt.label}
              variant={selectedOption === opt.label ? "default" : "outline"}
              size="sm"
              onClick={() => handleOptionSelect(opt)}
              className="text-xs h-7 px-3"
              data-testid={`filter-option-${opt.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      )}

      {activePeriod === "custom" && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="px-2 py-1 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
              data-testid="filter-custom-from"
            />
            <span className="text-sm text-slate-400">to</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="px-2 py-1 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
              data-testid="filter-custom-to"
            />
          </div>
          <Button
            size="sm"
            onClick={handleCustomApply}
            disabled={!customFrom || !customTo}
            className="text-xs h-7 px-4"
            data-testid="filter-custom-apply"
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
}
